'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (next: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Originally `useTheme` was a per-component `useState` hook, which meant the
 * topbar's toggle and the recipes/inbox pages each held isolated theme
 * state — toggling dark mode in the topbar updated `<html class="dark">`
 * (covering Tailwind dark variants) but the inline-styled pages kept their
 * mount-time `isDark` snapshot and looked light. Lifting state into a real
 * context keeps every consumer rendering against the same source of truth.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initial state must match what the SERVER renders (no `window` access)
  // otherwise React's hydration check fails for any component rendered with
  // inline tokens derived from `isDark`. The inline <script> in layout.tsx
  // already applied the dark class to <html> for Tailwind variants pre-
  // hydration; we sync our React state inside the mount effect below.
  const [theme, setThemeState] = useState<Theme>('light');

  useEffect(() => {
    const stored = (typeof window !== 'undefined'
      ? (localStorage.getItem('cb_theme') as Theme | null)
      : null);
    const resolved =
      stored ??
      (typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light');
    // SSR-safe theme bootstrap: server can't read localStorage, so the
    // initial state is 'light' and we flip on mount. This is the canonical
    // pattern for browser-only state that needs to feed back into React.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeState(resolved);
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', resolved === 'dark');
    }
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('cb_theme', next);
      document.documentElement.classList.toggle('dark', next === 'dark');
    }
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        localStorage.setItem('cb_theme', next);
        document.documentElement.classList.toggle('dark', next === 'dark');
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, toggle, setTheme }), [theme, toggle, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Defensive: if a component renders outside the provider (shouldn't happen
    // since ThemeProvider wraps the app at layout.tsx), fall back to light so
    // the UI renders something sane instead of throwing during dev hot-reload.
    return {
      theme: 'light',
      toggle: () => undefined,
      setTheme: () => undefined,
    };
  }
  return ctx;
}
